<script setup lang="ts">
import BetModal from "@/app/components/modal/BetModal.vue"
import CasinoBonusModal from "@/app/components/modal/CasinoBonusModal.vue"
import ChatModal from "@/app/components/modal/ChatModal.vue"
import BaseButton from "@/app/components/ui/BaseButton.vue"
import BaseIcon from "@/app/components/ui/BaseIcon.vue"
import { useBonuses } from "@/app/composables/useBonus"
import { useConfig } from "@/app/composables/useConfig"
import { useI18n } from "@/app/composables/useI18n"
import { useLanguageModal } from "@/app/composables/useLanguageModal"
import { useTooltip } from "@/app/composables/useTooltip"
import { countryMapping } from "@/app/shared/helpers/general.helper"
import { useTranslatedLogo } from "@/casino/composables/useBannerTranslation"
import MyBet from "@/user-account/components/MyBet.vue"
import UserWallet from "@/user-account/components/UserWallet.vue"
import { useAuth } from "@/user-account/composables/useAuth"
import { useUserService } from "@/user-account/composables/useUserService"
import { storeToRefs } from "pinia"
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import { useRoute } from "vue-router"
import { useBetStore } from "../../../../store"
import DepositResultModal from "../modal/DepositResultModal.vue"
import LoginModal from "../modal/LoginModal.vue"
import PasswordResetModal from "../modal/PasswordResetModal.vue"
import RegisterModal from "../modal/RegisterModal.vue"
import AppHeaderDivisionLinks from "./AppHeaderDivisionLinks.vue"

const route = useRoute()

const { isDrawerOpen } = useConfig()

const store = useBetStore()
const { showBonusModal } = storeToRefs(useBetStore())
const userStore = useUserService()
const { activeBonus } = useBonuses()
const { tooltipVisible, tooltipPosition, updateTooltipPosition, getTooltipText } = useTooltip()

const { t, locale } = useI18n()
const { openLanguageModal } = useLanguageModal()

const { isAuthenticated } = useAuth()

const showPasswordResetModal = ref(false)
const showRegisterModal = ref(false)
const showBetModal = ref(false)
const showDepositResultModal = ref(false)
const screenWidth = ref(window.innerWidth)

const showLoginModal = computed(() => store.login)
const isMobile = computed(() => {
  return store.getScreenWidth < 512
})

function handleResize() {
  screenWidth.value = window.innerWidth
  store.setScreenWidth(window.innerWidth)
}

const back = ref(false)

onMounted(() => {
  window.addEventListener("resize", handleResize)
  handleResize() // Initial check

  if (route.query.next_action === "showDepositResultModal") {
    showDepositResultModal.value = true
  }
})

function handleShowLoginModal() {
  store.handleShowLogin()
  if (back.value) {
    back.value = false
  }
}

function handleShowLoginWithBack() {
  store.handleShowLogin()
  back.value = true
}

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize)
})

function onPasswordReset() {
  showPasswordResetModal.value = true
  store.handleShowLogin()
}

function handleBonusModal() {
  if (userStore.isAuthenticated()) {
    store.setShowBonusModal(!showBonusModal.value)
  }
  else {
    store.handleShowLogin()
  }
}

const isVisibleDivisionLinks = computed(() => {
  if (store.getScreenWidth < 568) {
    return false
  }

  const modules = store.getModules

  return modules?.length > 1
})
const brandName = import.meta.env.VITE_APP_BRAND

const { logo } = useTranslatedLogo()
</script>

<template>
  <DepositResultModal
    :show="showDepositResultModal"
    @open-deposit-modal="() => (showDepositResultModal = true)"
    @close="() => (showDepositResultModal = false)"
  />

  <LoginModal
    :show="showLoginModal"
    :back="back"
    @reset-password="onPasswordReset"
    @close="handleShowLoginModal"
    @open-register-modal="showRegisterModal = true"
  />
  <RegisterModal
    :show="showRegisterModal"
    @open-login-modal="handleShowLoginWithBack"
    @close="() => (showRegisterModal = false)"
  />
  <PasswordResetModal
    :show="showPasswordResetModal"
    @close="() => (showPasswordResetModal = false)"
  />

  <ChatModal :show="store.chatModal" @close="store.handleShowChatModal" />
  <CasinoBonusModal :show="showBonusModal" :bonus="activeBonus" @close="handleBonusModal" />

  <header data-testid="app-header" class="app-header" @click="() => store.handleShowBetModal(false)">
    <div class="left-panel">
      <div
        v-if="!isMobile"
        data-testid="app-headerToggleBtn"
        :class="isDrawerOpen ? 'expand-btn-inner' : ''"
        @mouseenter="
          tooltipVisible = true;
          updateTooltipPosition($event);
        "
        @mouseleave="tooltipVisible = false"
      >
        <BaseIcon
          class="hamburger"
          :name="isDrawerOpen ? 'shift' : 'menu-open'"
          size="24"
          color="#eee"
          @click="
            () => {
              isDrawerOpen = !isDrawerOpen;
              tooltipVisible = false;
            }
          "
        />
      </div>
      <div class="logo">
        <router-link
          :class="brandName === 'sojibet' ? 'soji-logo' : 'brand-logo'"
          to="/"
        >
          <img
            :src="logo"
            alt=""
            :class="{ 'mobile-logo': screenWidth < 568 }"
          >
        </router-link>
      </div>
      <div
        v-if="screenWidth >= 568 && isAuthenticated()"
        class="bonus-btn"
        @click="handleBonusModal"
      >
        <img src="/assets/gift.svg" class="bonus-img">
        <div>{{ t('general.bonuses') }}</div>
      </div>
      <AppHeaderDivisionLinks v-if="isVisibleDivisionLinks" />
    </div>
    <div class="right-panel-wrapper">
      <UserWallet v-if="isAuthenticated()" @click.stop />

      <div v-if="!isAuthenticated()" class="right-panel">
        <div data-testid="header-registerBtn" class="register-btn" @click="() => (showRegisterModal = true)">
          {{ t('account.auth_register') }}
        </div>
        <BaseButton
          data-testid="header-LoginBtn"
          class="auth-button alight"
          @click="handleShowLoginModal"
          @click.stop
        >
          {{ t('account.auth_login') }}
        </BaseButton>
      </div>
      <div v-else data-testid="header-right-panel" class="right-panel">
        <div v-if="screenWidth >= 568">
          <MyBet @click.stop />
        </div>
      </div>
      <div class="language-btn notebook-only" @click="openLanguageModal">
        <img :src="`/assets/images/flags/${countryMapping[locale.flag] || 'xx'}.svg`" alt="" class="language-flag">
      </div>
    </div>
  </header>
  <BetModal
    @open-register-modal="() => (showRegisterModal = true)"
    @close="() => (showBetModal = false)"
  />
  <Teleport to="body">
    <div
      v-if="tooltipVisible"
      class="tooltip-teleport"
      :style="`
        position: fixed;
        left: ${tooltipPosition.x + 10}px;
        top: ${tooltipPosition.y}px;
        z-index: 9999;
        transform: translateY(-50%);
        background-color: white;
        color: #000;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 14px;
        white-space: nowrap;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      `"
    >
      {{ getTooltipText }}
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.app-header {
  background-color: var(--be-small-card-bg);
  display: flex;
  justify-content: space-between;
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 10;
  padding: 0 5px;
  height: 60px;
  min-width: 100%;
  width: 100%;

  .language-btn {
    width: 40px;
    height: 40px;
    background-color: var(--be-secondary-btn);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius:4px;
    margin-right: 10px;
    cursor: pointer;

  }

  .language-flag {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
  }

  .expand-btn-inner {
    flex: 0 0 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 36px;
    min-height: 34px;
    margin-left: 5px;

    > div {
      margin: 0 !important;
    }
  }

  .block {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    background: #333360;
    height: 40px;
    width: 40px;
    cursor: pointer;
  }

  .register-btn {
    font-family: Poppins;
    font-size: 15px;
    border-radius: 4px;
    width: 95.8px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    &:hover {
      height: 36px;
    }

    @media screen and (max-width: 390px) {
      width: 55%;
    }
  }

  .left-panel {
    display: flex;
    align-items: center;

    .hamburger {
      margin: 0 10px;
      cursor: pointer;
    }

    .bonus-btn,
    .logo {
      display: flex;
      align-items: center;
    }

    .bonus-btn {
      border-radius: 4px;
      gap: 5px;
      background: var(--Linear, linear-gradient(180deg, #fff9a8 0%, #ffaefe 100%));
      width: 150px;
      height: 36px;
      color: #213743;
      font-family: Rubik-med;
      font-size: 14px;
      position: relative;
      margin-left: 17px;
      cursor: pointer;

      img {
        display: block;
        max-width: 100%;
        max-height: 35px;
        padding-left: 20px;

        .tablet & {
          padding-left: 0;
        }

        &.mobile-logo {
          max-width: 170px;
        }
      }

      @media screen and (max-width: 580px) {
        width: 40px;
        height: 40px;
        margin-left: 0;

        .bonus-img {
          width: 26px;
          left: 3px;
          right: initial;
        }
      }
    }

    .logo {
      margin-right: 20px;

      img {
        display: block;
        max-width: 100%;
        max-width: 200px;
        max-height: 35px;
        padding-left: 20px;

        .tablet & {
          padding-left: 0;
        }

        &.mobile-logo {
          max-width: 170px;
        }

        @media screen and (max-width: 390px) {
          &.mobile-logo {
            padding-left: 8px;
          }
        }
        @media screen and (max-width: 380px) {
          &.mobile-logo {
            max-width: 130px;
          }
        }
        @media screen and (max-width: 360px) {
          &.mobile-logo {
            max-width: 120px;
          }
        }
        @media screen and (max-width: 347px) {
          &.mobile-logo {
            max-width: 100px;
          }
        }
      }

      @media screen and (max-width: 580px) {
        margin-right: 0;

        img {
          padding-left: 16px;
        }
      }
    }

    @media screen and (max-width: 390px) {
      width: 35%;
    }
  }

  .right-panel-wrapper {
    display: flex;
    align-items: center;

    .language-selector {
      flex-shrink: 0;
      margin: 0 10px 0 0;
      position: absolute;
      left: 271px;
      top: 61px;
      background: var(--be-bg);
      overflow: auto;
    }

    .right-panel {
      display: flex;
      align-items: center;

      .header-wallet-select {
        width: 150px;
      }

      .wallet-button {
        margin-right: 7px;
        display: flex;
        align-items: center;
        height: 40px;
        padding: 0 15px;
        border-radius: 2px;
        margin-top: -1px;
        cursor: pointer;

        .title {
          margin-left: 4px;
        }
      }

      .auth-button {
        text-transform: uppercase;
        margin: 0 8px 0 0;
        font-size: 0.8em;

        @media screen and (max-width: 390px) {
          width: 100%;
          padding: 0;
          margin-left: 10px;
        }
      }

      @media screen and (max-width: 390px) {
        margin: 0;
      }
    }

    @media screen and (max-width: 390px),
    screen and (max-width: 360px) {
      justify-content: flex-end;
      margin-right: 10px;
    }
  }

  @media screen and (max-width: 360px) {
    .right-panel-wrapper {
      justify-content: flex-end;
      margin-right: 10px;
    }
  }

@media screen and (max-width: 580px) {
  .app-header {
    align-items: center;
}}}
</style>
